import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosInstance from '../config/axios';
import { useAuthContext } from './AuthContext';
import { UserRoleData, UserRole, RoleErrorCode } from '../hooks/useUserRole';

interface UserRoleContextValue {
  role: UserRole | null;
  workAreaIds: number[];
  isAdmin: boolean;
  isManager: boolean;
  isEmployee: boolean;
  permissions: {
    can_view_profile: boolean;
    can_view_documents: boolean;
  };
  loading: boolean;
  error: Error | null;
  errorCode: RoleErrorCode | null;
}

const defaultValue: UserRoleContextValue = {
  role: null,
  workAreaIds: [],
  isAdmin: false,
  isManager: false,
  isEmployee: false,
  permissions: { can_view_profile: false, can_view_documents: false },
  loading: true,
  error: null,
  errorCode: null,
};

const UserRoleContext = createContext<UserRoleContextValue>(defaultValue);

export function UserRoleProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, selectedTenant } = useAuthContext();
  const [roleData, setRoleData] = useState<UserRoleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [errorCode, setErrorCode] = useState<RoleErrorCode | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    async function fetchUserRole() {
      try {
        const response = await axiosInstance.get<UserRoleData>('/api/me/roles/');
        setRoleData(response.data);
        setError(null);
        setErrorCode(null);
      } catch (err: any) {
        console.error('Failed to fetch user role:', err);

        const responseData = err?.response?.data;
        const code = responseData?.error || responseData?.code || 'unknown';

        let mappedCode: RoleErrorCode = 'unknown';
        if (code === 'tenant_header_missing') {
          mappedCode = 'tenant_header_missing';
        } else if (code === 'tenant_user_missing') {
          mappedCode = 'tenant_user_missing';
        } else if (code === 'insufficient_role') {
          mappedCode = 'insufficient_role';
        }

        setError(err as Error);
        setErrorCode(mappedCode);
      } finally {
        setLoading(false);
      }
    }

    fetchUserRole();
  }, [isAuthenticated, selectedTenant?.tenant_id]);

  const value: UserRoleContextValue = {
    role: roleData?.role || null,
    workAreaIds: roleData?.work_area_ids || [],
    isAdmin: roleData?.role === 'ADMIN',
    isManager: roleData?.role === 'MANAGER',
    isEmployee: roleData?.role === 'EMPLOYEE',
    permissions: {
      can_view_profile: roleData?.permissions?.can_view_profile ?? false,
      can_view_documents: roleData?.permissions?.can_view_documents ?? false,
    },
    loading,
    error,
    errorCode,
  };

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

export function useUserRoleContext(): UserRoleContextValue {
  return useContext(UserRoleContext);
}
